/**
 * ICU `select` key meaning "render this message without a date range". Only meaningful in a
 * message that declares a `none` branch — see {@link dateRangeSelectValue}.
 */
export const NO_DATE_RANGE = 'none';

/**
 * Shape of a stored range, produced by `InAppNotificationLogic.timesheetApprovalNotifyInApp`
 * as `periodStart.toString() + " - " + periodEnd.toString()` over two `LocalDate` values. A
 * year that does not fit four digits serializes with a sign prefix, which is how an unbounded
 * period shows up (`+999999999-12-31`).
 *
 * This mirrors a format owned by that method: if the separator or the serialization there
 * changes, every timesheet notification silently loses its date range, so the two must move
 * together.
 */
const ISO_DATE_RANGE = /^\d{4}-\d{2}-\d{2} - \d{4}-\d{2}-\d{2}$/;

/**
 * The `dateRange` value to feed an ICU message that has a `none` branch — either the stored
 * range, or {@link NO_DATE_RANGE} when there is none worth showing.
 *
 * Only the six `notification.timesheet_*` messages declare that branch. The nine
 * `notification.time_off_*` messages interpolate `{dateRange}` directly, so passing this
 * function's output to one of those renders the literal word "none" in the sentence. Use it
 * only where the message can express an absent range.
 *
 * Approval actions covering no bounded period — submitting time off sends every outstanding
 * registration rather than a week — no longer report a range at all. Notifications stored
 * before that carry the open-ended bounds instead, rendering as
 * `+999999999-12-25 - +999999999-12-31`, and a lock date can leave a half-bounded
 * `2026-05-01 - +999999999-12-31`. Anything that is not a pair of representable dates is
 * treated as no range, so those read the same as notifications written today.
 *
 * This guard is permanent, not a migration shim. `person_notification` rows are never deleted,
 * and the bell reads the newest 500 by id per subscriber, so for a low-volume user the affected
 * rows stay in view indefinitely. Removing this only becomes safe once those stored values have
 * been rewritten.
 *
 * @param dateRange the raw `dateRange` notification param, if present
 */
export function dateRangeSelectValue(dateRange: unknown): string {
	return typeof dateRange === 'string' && ISO_DATE_RANGE.test(dateRange) ? dateRange : NO_DATE_RANGE;
}
